import { FC, ReactElement } from 'react';

import { Icon, ProductPrice } from '../../../../components';
import { Button, IconButton } from '../../../../compositions';
import { UspItem } from '../../../../entities/@blocks/UspSection/UspSection';
import { AddCouponCodeToOrderFormData } from '../../../../entities/@forms/CouponCodeForm/CouponCodeForm';
import { OrderInterface } from '../../../../entities/@shop/Order/Order';
import { HorizontalAlignment } from '../../../../entities/Alignment/Alignment';
import { useDeviceWidth, useTrans } from '../../../../hooks';
import { CartItemForm } from '../../../@forms/CartItemForm/CartItemForm';
import { CartItemFormSkeletons } from '../../../@forms/CartItemForm/skeletons';
import { ProductQuantitySubmitFormData } from '../../../@forms/CartItemForm/validations';
import { PaymentMethods } from '../PaymentMethods/PaymentMethods';
import { Pricing } from '../Pricing/Pricing';

import './StartStep.scss';

interface StartStepProps extends OrderInterface {
    isInitiallyLoading: boolean;
    isLoading: boolean;
    uspItems: UspItem[];
    onGoToCheckout: () => void;
    onUpdateCartItem: (formData: ProductQuantitySubmitFormData) => void;
    onRemoveItemFromCart: (cartItemId: string) => void;
    onClearCart: () => void;
    onSetCouponCode: (data: AddCouponCodeToOrderFormData) => void;
    onRemoveCouponCode: () => void;
    className?: string;
}

export const StartStep: FC<StartStepProps> = ({
    isInitiallyLoading,
    isLoading,
    uspItems,
    items,
    subTotalPrice,
    handlingPrice,
    totalPrice,
    discounts,
    onGoToCheckout,
    onUpdateCartItem,
    onRemoveItemFromCart,
    onClearCart,
    onSetCouponCode,
    onRemoveCouponCode,
    className = '',
}): ReactElement => {
    const trans = useTrans();
    const { isMobile } = useDeviceWidth();

    return (
        <div className={`start-step ${className}`}>
            <details open={!isMobile} className="start-step__left-section">
                <summary className="start-step__accordion-summary">
                    <div className="start-step__accordion-heading">
                        <h2 className="start-step__heading">
                            {trans('common.cart')}
                        </h2>

                        <Icon name="chevron-down" className="start-step__accordion-icon" />
                    </div>

                    <ProductPrice
                        isCents
                        includeCurrencySymbol
                        current={totalPrice}
                        original={totalPrice}
                        className="start-step__accordion-price"
                    />
                </summary>

                <section className="start-step__left-section-wrapper">
                    <div className="start-step__top">
                        <h2 className="start-step__heading">
                            {trans('common.cart')}
                        </h2>

                        {items.length !== 0 && (
                            <Button
                                text={trans('containers.checkout.startStep.clear')}
                                onClick={onClearCart}
                                className="start-step__clear-cart-button"
                            />
                        )}
                    </div>

                    {isInitiallyLoading && (
                        <CartItemFormSkeletons />
                    )}

                    <div className="start-step__product-list">
                        {items.map(item => (
                            <CartItemForm
                                key={item.id}
                                {...item}
                                showBigRemoveButton
                                isEditMode
                                isLoading={isLoading}
                                onUpdateCartItem={onUpdateCartItem}
                                onRemoveItemFromCart={onRemoveItemFromCart}
                                className="start-step__product"
                            />
                        ))}

                        {!isLoading && items.length === 0 && (
                            <div className="start-step__no-products-wrapper">
                                <Icon name="cart" className="start-step__no-products-icon" />

                                <p className="start-step__no-products-text">
                                    {trans('containers.checkout.startStep.noProductsInCart')}
                                </p>
                            </div>
                        )}
                    </div>
                </section>
            </details>

            <section className="start-step__right-section">
                <h2 className="start-step__heading">
                    {trans('containers.checkout.startStep.yourOrder')}
                </h2>

                <Pricing
                    hideCouponCodeInput
                    isLoading={isLoading}
                    subTotalPrice={subTotalPrice}
                    handlingPrice={handlingPrice}
                    totalPrice={totalPrice}
                    discounts={discounts}
                    onSetCouponCode={onSetCouponCode}
                    onRemoveCouponCode={onRemoveCouponCode}
                    className="start-step__prices-wrapper"
                />

                <IconButton
                    isSmall
                    hasAnimation
                    icon="arrow-right"
                    iconPos={HorizontalAlignment.right}
                    text={trans('containers.checkout.startStep.continueToDetails')}
                    disabled={!items.length}
                    onClick={onGoToCheckout}
                    className="start-step__button"
                />

                <PaymentMethods />

                <ul className="start-step__usp-list">
                    {uspItems.map(item => (
                        <li key={item.id} className="start-step__usp-item">
                            {item.icon && (
                                <Icon name={item.icon} className="start-step__usp-item-icon" />
                            )}

                            {item.text}
                        </li>
                    ))}
                </ul>
            </section>
        </div>
    );
};
